// «Аккаунт и тариф» (um/2026-10-29/01 п. 12, decision 031): use against the plan, the term and its state, payments; the owner
// renews, changes the plan and space, freezes and asks to delete the account. A payment goes to the gateway and comes back here.

import { useEffect, useState } from 'react';
import { CompactTable } from '../table/CompactTable.tsx';
import { PageBlock } from '../ui/Page.tsx';
import { Button, ConfirmDialog, Dialog, Field, Input, Menu, MenuContent, MenuItem, MenuTrigger, cx, useToast } from '../ui/primitives.tsx';
import s from './account.module.css';

interface Use { n: number; max: number | null }
interface View {
  slug: string; owner: boolean; state: string; stateLabel: string; paidUntil: string | null; graceDays: number; deleteAfter: string | null;
  frozen: { since: string; leftDays: number } | null;
  plan: { key: string; name: string; price: number; storageGb: number } | null; nextPlan: string | null;
  extraGb: number; nextExtraGb: number | null; pack: { gb: number; price: number; now: number };
  usage: { users: Use; types: { name: string; n: number; max: number }[]; records: Use; storage: Use; byType: { name: string; n: number }[] };
  renew: { months: number; amount: number }[];
  plans: { key: string; name: string; price: number; lines: string[]; current: boolean; now: number; later: boolean }[];
  payments: { id: string; created_at: string; purposeLabel: string; description: string; amount: number; status: string; statusLabel: string; provider: string }[];
}

const rub = (kop: number) => `${Math.round(kop / 100).toLocaleString('ru-RU')} ₽`;
const day = (d: string | null) => (d ? new Date(d).toLocaleDateString('ru-RU') : '—');
const GB = 1024 ** 3;
const gb = (b: number) => `${(b / GB).toLocaleString('ru-RU', { maximumFractionDigits: b < GB ? 2 : 1 })} ГБ`;
const num = (n: number) => n.toLocaleString('ru-RU');
const MONTHS: Record<number, string> = { 1: '1 месяц', 3: '3 месяца', 12: '12 месяцев' };
const TONE: Record<string, string> = { active: s.ok!, grace: s.warn!, readonly: s.bad!, frozen: s.warn!, deleting: s.bad!, blocked: s.bad!, free: '' };
const PAY_TONE: Record<string, string> = { succeeded: s.ok!, failed: s.bad!, paid: s.warn! };

async function call(body: unknown): Promise<{ payUrl?: string; message?: string; error?: { message: string } }> {
  const r = await fetch('/api/account/billing', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).catch(() => null);
  if (!r) return { error: { message: 'Сервер недоступен. Проверьте соединение.' } };
  return r.json();
}

export function AccountScreen() {
  const toast = useToast();
  const [v, setV] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [plansOpen, setPlansOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [freezing, setFreezing] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = async (payment?: string | null) => {
    const r = await fetch(`/api/account/billing${payment ? `?payment=${payment}` : ''}`).catch(() => null);
    const j = r ? await r.json().catch(() => null) as (View & { error?: { message: string } }) | null : null;
    if (!r?.ok || !j) { setError(j?.error?.message ?? 'Не удалось загрузить аккаунт'); return null; }
    setV(j);
    return j;
  };
  useEffect(() => {
    // Back from the gateway with «?payment=…»: the outcome is said once and the address is cleaned.
    const payment = new URLSearchParams(location.search).get('payment');
    void load(payment).then((j) => {
      if (!payment || !j) return;
      history.replaceState(null, '', location.pathname);
      const p = j.payments.find((x) => x.id === payment);
      if (p?.status === 'succeeded') toast({ kind: 'info', text: `Оплата прошла: ${p.description}` });
      else if (p) toast({ kind: 'error', text: `Оплата не прошла — ${p.statusLabel.toLowerCase()}. Деньги не списаны.` });
    });
  }, []);
  const act = async (body: Record<string, unknown>) => {
    const r = await call(body);
    if (r.error) { toast({ kind: 'error', text: r.error.message }); return false; }
    if (r.payUrl) { location.href = r.payUrl; return true; }
    if (r.message) toast({ kind: 'info', text: r.message });
    await load();
    return true;
  };

  if (error) return <p className={s.muted}>{error}</p>;
  if (!v) return <p className={s.muted}>Загрузка…</p>;
  const u = v.usage;
  const working = ['active', 'grace', 'readonly', 'free'].includes(v.state);
  const graceEnd = v.paidUntil ? new Date(new Date(v.paidUntil).getTime() + v.graceDays * 864e5).toISOString() : null;
  return (
    <>
      {!v.owner && <p className={s.muted}>Тариф и оплату меняет владелец аккаунта; здесь — просмотр.</p>}
      {v.state === 'grace' && <div className={cx(s.banner, s.warn)} role="status">Срок оплаты закончился {day(v.paidUntil)}. До {day(graceEnd)} всё работает, затем аккаунт откроется только для чтения. Продлите тариф.</div>}
      {v.state === 'readonly' && <div className={cx(s.banner, s.bad)} role="alert">Аккаунт открыт только для чтения: срок оплаты закончился {day(v.paidUntil)}. Данные целы — после оплаты всё снова работает.</div>}

      <PageBlock title="Тариф" rows label="Тариф">
        <Field as="div" inline="end" label={v.plan?.name ?? '—'} hint={v.plan?.price ? `${rub(v.plan.price * 100)} в месяц` : 'Бесплатно'}>
          <span className={cx(s.state, TONE[v.state])}>{v.stateLabel}</span>
        </Field>
        <Field as="div" inline="end" label="Оплачено до"><span>{v.paidUntil ? day(v.paidUntil) : 'без срока'}</span></Field>
        {v.nextPlan && <Field as="div" inline="end" label="Со следующего периода"><span>{v.nextPlan}</span></Field>}
        <Field as="div" inline="end" label="Дополнительное место"
          hint={v.owner && v.plan?.price ? `Пакет — ${v.pack.gb} ГБ за ${num(v.pack.price)} ₽ в месяц. Повышение тарифа и место — сразу, с доплатой за остаток срока; понижение — со следующего периода.` : undefined}>
          <span>{v.extraGb} ГБ{v.nextExtraGb !== null ? ` → ${v.nextExtraGb} ГБ с ${day(v.paidUntil)}` : ''}</span>
        </Field>
        {v.owner && working && (
          <div className={s.actions}>
            {!!v.plan?.price && (
              <Menu>
                <MenuTrigger asChild><Button variant="primary" icon="money">Продлить</Button></MenuTrigger>
                <MenuContent>{v.renew.map((r) => <MenuItem key={r.months} onSelect={() => void act({ action: 'renew', months: r.months })}>{MONTHS[r.months]} — {rub(r.amount)}</MenuItem>)}</MenuContent>
              </Menu>
            )}
            <Button variant="secondary" onClick={() => setPlansOpen(true)}>Сменить тариф</Button>
            {!!v.plan?.storageGb && (
              <Menu>
                <MenuTrigger asChild><Button variant="secondary">Место</Button></MenuTrigger>
                <MenuContent>
                  <MenuItem icon="plus" onSelect={() => void act({ action: 'storage-add' })}>+{v.pack.gb} ГБ сейчас — {v.pack.now >= 100 ? rub(v.pack.now) : 'без доплаты'}</MenuItem>
                  {(v.nextExtraGb ?? v.extraGb) > 0 && <MenuItem onSelect={() => void act({ action: 'storage-remove' })}>−{v.pack.gb} ГБ со следующего периода</MenuItem>}
                </MenuContent>
              </Menu>
            )}
          </div>
        )}
      </PageBlock>

      <PageBlock title="Использование" rows label="Использование">
        <Meter label="Люди в команде" n={u.users.n} max={u.users.max} />
        {u.types.map((x) => <Meter key={x.name} label={x.name} n={x.n} max={x.max} />)}
        {u.records.max !== null && <Meter label="Записи всего" n={u.records.n} max={u.records.max} />}
        <Meter label="Место для файлов" n={u.storage.n} max={u.storage.max} fmt={gb} />
        <div className={s.byType}>
          <span className={s.rowName}>Записи по разделам</span>
          {u.byType.length ? <CompactTable rows={u.byType} rowKey={(x) => x.name} columns={[
            { key: 'name', label: 'Раздел', render: (x) => x.name },
            { key: 'n', label: 'Записей', render: (x) => num(x.n), className: s.num },
          ]} /> : <span className={s.muted}>Пока нет</span>}
        </div>
      </PageBlock>

      <PageBlock title="Платежи" label="Платежи">
        {v.payments.length ? (
          <CompactTable rows={v.payments} rowKey={(p) => p.id} columns={[
            { key: 'at', label: 'Когда', render: (p) => day(p.created_at) },
            { key: 'what', label: 'За что', render: (p) => p.description },
            { key: 'sum', label: 'Сумма', render: (p) => rub(p.amount), className: s.num },
            { key: 'st', label: 'Состояние', render: (p) => <span className={cx(s.state, PAY_TONE[p.status])}>{p.statusLabel}{p.provider === 'test' ? ' · тест' : p.provider === 'manual' ? ' · по счёту' : ''}</span> },
          ]} />
        ) : <p className={s.muted}>Платежей пока нет.</p>}
      </PageBlock>

      {v.owner && working && (
        <PageBlock title="Опасная зона" tone="danger" rows label="Опасная зона">
          {v.state === 'active' && (
            <Field as="div" inline="end" label="Заморозить аккаунт" hint="Вход закроется для всех, оплаченные дни сохранятся до разморозки (не дольше 90 дней). Разморозить можно по ссылке на странице входа.">
              <Button variant="secondary" onClick={() => setFreezing(true)}>Заморозить</Button>
            </Field>
          )}
          <Field as="div" inline="end" label="Удалить аккаунт" hint="Аккаунт закроется сразу, данные удалятся через 30 дней. До этого удаление отменяет поддержка.">
            <Button variant="danger" onClick={() => setDeleting(true)}>Удалить аккаунт</Button>
          </Field>
        </PageBlock>
      )}

      <PlansDialog open={plansOpen} onClose={() => setPlansOpen(false)} v={v} onPick={async (key) => { if (await act({ action: 'plan', plan: key })) setPlansOpen(false); }} />
      <ConfirmDialog open={freezing} title="Заморозить аккаунт?" action="Заморозить" danger busy={busy} onClose={() => setFreezing(false)}
        onConfirm={() => { setBusy(true); void act({ action: 'freeze' }).then((ok) => { setBusy(false); setFreezing(false); if (ok) location.reload(); }); }}>
        <p>Вход закроется для всей команды сразу. Оплаченные дни сохранятся до разморозки, но не дольше 90 дней.</p>
        <p>Разморозить можно по ссылке на странице входа.</p>
      </ConfirmDialog>
      <DeleteDialog open={deleting} slug={v.slug} onClose={() => setDeleting(false)} onDelete={async (confirmSlug) => {
        const r = await call({ action: 'delete', confirm: confirmSlug });
        if (r.error) { toast({ kind: 'error', text: r.error.message }); return; }
        // The workspace is closed now: the app starts again and shows the closed page with the date of deletion.
        location.reload();
      }} />
    </>
  );
}

function Meter({ label, n, max, fmt = num }: { label: string; n: number; max: number | null; fmt?: (x: number) => string }) {
  const over = max !== null && n > max;
  return (
    <Field as="div" inline="end" label={label}>
      <span className={s.meterCell}>
        <span className={cx(over && s.overText)}>{fmt(n)}{max !== null ? ` из ${fmt(max)}` : ''}</span>
        {max !== null && <span className={cx(s.meter, over && s.over)} aria-hidden><span style={{ width: `${max ? Math.min(100, Math.round((n / max) * 100)) : 100}%` }} /></span>}
      </span>
    </Field>
  );
}

function PlansDialog({ open, onClose, v, onPick }: { open: boolean; onClose: () => void; v: View; onPick: (key: string) => void }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }} label="Сменить тариф">
      <div className={s.dialog}>
        <h2 className={s.dialogTitle}>Сменить тариф</h2>
        <p className={s.muted}>Тариф выше начинает действовать сразу, доплата — за остаток оплаченного срока. Тариф ниже — со следующего периода, если команда и данные в него помещаются.</p>
        <div className={s.plans}>
          {v.plans.map((p) => (
            <div key={p.key} role="group" aria-label={p.name} className={cx(s.plan, p.current && s.planOn)}>
              <div className={s.planName}>{p.name}</div>
              <div className={s.planPrice}>{p.price ? `${rub(p.price * 100)} в месяц` : 'Бесплатно'}</div>
              <ul className={s.planList}>
                {p.lines.map((l) => <li key={l}>{l}</li>)}
              </ul>
              {p.current ? <span className={s.muted}>{v.nextPlan ? <Button size="sm" onClick={() => onPick(p.key)}>Остаться на нём</Button> : 'Текущий'}</span>
                : <Button size="sm" variant={p.later ? 'secondary' : 'primary'} onClick={() => onPick(p.key)}>{p.later ? (v.paidUntil && v.state === 'active' ? `С ${day(v.paidUntil)}` : 'Перейти') : p.now >= 100 ? `Перейти — ${rub(p.now)}` : 'Перейти'}</Button>}
            </div>
          ))}
        </div>
        <div className={s.dialogFoot}><Button onClick={onClose}>Закрыть</Button></div>
      </div>
    </Dialog>
  );
}

function DeleteDialog({ open, slug, onClose, onDelete }: { open: boolean; slug: string; onClose: () => void; onDelete: (slug: string) => void }) {
  const [typed, setTyped] = useState('');
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setTyped(''); onClose(); } }} label="Удалить аккаунт">
      <div className={s.dialog}>
        <h2 className={s.dialogTitle}>Удалить аккаунт</h2>
        <p className={s.muted}>Вход закроется для всей команды сразу. Данные хранятся 30 дней и удаляются; до этого срока удаление отменяет поддержка ConfDeck. Перед удалением можно выгрузить данные: «Профиль → Данные».</p>
        <Field label={`Введите адрес аккаунта: ${slug}`}><Input value={typed} onChange={(e) => setTyped(e.target.value)} aria-label="Адрес аккаунта" autoFocus /></Field>
        <div className={s.dialogFoot}><Button onClick={onClose}>Отмена</Button><Button variant="danger" disabled={typed.trim().toLowerCase() !== slug} onClick={() => onDelete(typed)}>Удалить аккаунт</Button></div>
      </div>
    </Dialog>
  );
}
